// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { PaginationModel, ProductDto } from "@insite/client-framework/Types/ApiModels";
import { ReorderPadApiParameter, ReorderPadModel } from "@steiner/App/Services/ReorderPad";
import { Draft } from "immer";

export interface ReorderPadItemModel {
    productDescription?: string;
    lastOrderedQty?: string;
    lastOrderedDate?: string;
    productErpNumber?: string;
    unitOfMeasure?: string;
    productDto?: ProductDto;
    lineStatus?: string;
}

export type ReorderPadState = {
    orders?: ReorderPadItemModel[];
    isLoading?: boolean;
    pagination: PaginationModel | null;
    searchParams?: ReorderPadApiParameter;
};

const initialState: ReorderPadState = {
    isLoading: false,
    pagination: null,
    searchParams: {
        customerSequence: "-1",
        customerPartNumber: "",
        fromDate: "",
        toDate: "",
        sortBy: "",
        mfgrNumber: "",
        productDescription: "",
    },
};

const xReorderPad = createTypedReducerWithImmer(initialState, {
    "xReorderPad/BeginLoadingReorderPad": (draft: Draft<ReorderPadState>, action: { isLoading: boolean }) => {
        draft.isLoading = action.isLoading;
    },
    "xReorderPad/FailedLoadingReorderPad": (draft: Draft<ReorderPadState>) => {
        draft.isLoading = false;
    },
    "xReorderPad/CompleteLoadingReorderPad": (draft: Draft<ReorderPadState>, action: { value: ReorderPadModel }) => {
        draft.orders = action.value.reorderPadItemList;
        draft.pagination = action.value.pagination;
        draft.isLoading = false;
    },
    "xReorderPad/UpdateSearchParams": (
        draft: Draft<ReorderPadState>,
        action: { parameter: { key: string; value: string | number } },
    ) => {
        const { key, value } = action.parameter;
        // Update the searchParams with the new key and value
        draft.searchParams = { ...draft.searchParams, [key]: value };

        // Remove empty or undefined parameters
        for (const key in draft.searchParams) {
            const value = (draft.searchParams as any)[key];

            if (value === "" || value === undefined) {
                delete (draft.searchParams as any)[key];
            }
        }

        // If page size or page is changed, reset the page to 1
        if (
            draft.pagination &&
            draft.pagination.page &&
            draft.pagination.page > 1 &&
            key !== "page" &&
            key !== "pageSize"
        ) {
            draft.pagination.page = 1;
        }
    },
    "xReorderPad/ClearSearchParams": (draft: Draft<ReorderPadState>) => {
        draft.searchParams = { ...initialState.searchParams };
    },
});

(reducers as any).xReorderPad = xReorderPad;

export type xReorderPadState = {
    xReorderPad: ReorderPadState;
} & ApplicationState;

type ReorderPadReducer = {
    reorderPad: typeof xReorderPad;
};

export type ReorderPadDataActions = Parameters<ReorderPadReducer[keyof ReorderPadReducer]>[1] | AnyAction;
